// frontend/src/pages/Spot.tsx
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import { CopyIcon, RepeatIcon, AddIcon } from '@chakra-ui/icons'
import { IoMdSave } from 'react-icons/io'
import {
  Box,
  Button,
  Text,
  Stack,
  Input,
  useToast,
  HStack,
  VStack,
  IconButton,
  CloseButton,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  ButtonGroup,
  Tag,
  Wrap,
  WrapItem,
  Skeleton,
  useBreakpointValue
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { useEffect, useMemo, useState } from 'react'
import { updateTradingSpot, getCoinKrwPrice } from '../services/apiService'
import { useAuthState } from '../utils/authUtils'
import ApiManagerSpot from '../components/ApiManagerSpot'
import SizeCalculatorSpot from '../components/SizeCalculatorSpot'

type RoiTrigger = {
  roi: string
  size: string
}

const AVAILABLE_SYMBOLS = ['ETH', 'DOGE', 'SOL', 'BTC', 'XRP'] as const
type SpotSymbol = (typeof AVAILABLE_SYMBOLS)[number]

const SYMBOL_LABEL: Record<SpotSymbol, string> = {
  ETH: '이더리움',
  DOGE: '도지',
  SOL: '솔라나',
  BTC: '비트코인',
  XRP: '리플'
}

const Spot = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { reloadUser } = useAuthState()

  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose
  } = useDisclosure()

  const [confirmAction, setConfirmAction] = useState<'start' | 'stop' | null>(
    null
  )

  // ✅ 반응형 분기
  const isMobile = useBreakpointValue({ base: true, md: false })

  // ✅ 코인 선택 (기본: ETH)
  const [symbol, setSymbol] = useState<SpotSymbol>('ETH')

  // ✅ 현물: 레버리지/방향 제거
  const [takeProfit, setTakeProfit] = useState('10')
  const [stopLoss, setStopLoss] = useState('0')
  const [size, setSize] = useState('1') // 1 Size = 5,000 KRW
  const [roiTriggers, setRoiTriggers] = useState<RoiTrigger[]>([])
  const [useStopLoss, setUseStopLoss] = useState(false)

  const [isRunning, setIsRunning] = useState(false)

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [batchCount, setBatchCount] = useState('100')
  const [batchRoi, setBatchRoi] = useState('30')
  const [batchSize, setBatchSize] = useState('1')

  // ✅ 업비트 API Key 2종
  const [upbitAccessKey, setUpbitAccessKey] = useState('')
  const [upbitSecretKey, setUpbitSecretKey] = useState('')

  const SIZE_KRW = 5000
  const computedOrderKRW = Number(size || '0') * SIZE_KRW

  // ✅ 선택 심볼 현재가 (KRW)
  const [krwPrice, setKrwPrice] = useState<number | null>(null)
  const [loadingPrice, setLoadingPrice] = useState(false)

  // 배열에서 해당 심볼의 Spot 레코드 찾기
  const getSpotBySymbol = (list: any[] | undefined, sym: SpotSymbol) =>
    (list || []).find((s) => s?.symbol === sym)

  const fetchKrwPrice = async (sym: SpotSymbol) => {
    try {
      setLoadingPrice(true)
      const { data: res } = await getCoinKrwPrice(sym)
      if (res?.statusCode === 200) {
        setKrwPrice(res.data ?? null)
      } else {
        setKrwPrice(null)
      }
    } catch {
      setKrwPrice(null)
    } finally {
      setLoadingPrice(false)
    }
  }

  useEffect(() => {
    const list = auth?.spot || []

    const hasCurrent = list.some((s) => s?.symbol === symbol)

    if (!hasCurrent) {
      const first = list.find((s) =>
        AVAILABLE_SYMBOLS.includes((s?.symbol as SpotSymbol) || 'ETH')
      )?.symbol as SpotSymbol | undefined
      setSymbol(first || 'ETH')
    }

    setUpbitAccessKey(auth?.apiKeySpot ?? '')
    setUpbitSecretKey(auth?.apiSecretSpot ?? '')
  }, [auth])

  useEffect(() => {
    const s = getSpotBySymbol(auth?.spot, symbol) as any | undefined

    if (s) {
      setTakeProfit(String(s.tp ?? 10))
      setStopLoss(String(s.sl ?? 0))
      setSize(String(s.size ?? 1))
      setUseStopLoss(Boolean((s.sl ?? 0) > 0))
      setIsRunning(!!s.botStatus)

      if (Array.isArray(s.dynamic) && s.dynamic.length > 0) {
        setRoiTriggers(
          s.dynamic.map((item: any) => ({
            roi: String(item.roi),
            size: String(item.size)
          }))
        )
      } else {
        setRoiTriggers([])
      }
    } else {
      setTakeProfit('10')
      setStopLoss('0')
      setSize('1')
      setUseStopLoss(false)
      setIsRunning(false)
      setRoiTriggers([])
    }
  }, [symbol, auth?.spot])

  useEffect(() => {
    fetchKrwPrice(symbol)
  }, [symbol])

  const estimatedCoinAmount = useMemo(() => {
    if (!krwPrice || krwPrice <= 0) return null
    return computedOrderKRW / krwPrice
  }, [computedOrderKRW, krwPrice])

  const handleSave = async () => {
    try {
      const { data: res } = await updateTradingSpot({
        symbol,
        botStatus: null,
        takeProfit: Number(takeProfit),
        stopLoss: Number(stopLoss),
        size: Number(size),
        roiTriggers: roiTriggers.map((item) => ({
          roi: Number(item.roi),
          size: Number(item.size)
        })),
        useStopLoss: useStopLoss,
        upbitAccessKey,
        upbitSecretKey
      })
      if (res.statusCode === 200) {
        toast({
          title: t('현물 자동매매 설정'),
          description: res.message,
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('현물 자동매매 설정'),
        description:
          err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const handleToggleAutoTrade = async () => {
    if (isRunning) {
      // 종료
      try {
        const { data: res } = await updateTradingSpot({
          symbol,
          botStatus: false
        })
        if (res.statusCode === 200) {
          toast({
            title: t('봇 종료'),
            description: res.message,
            status: 'success',
            duration: 2000,
            isClosable: true
          })
          reloadUser()
        }
      } catch (err: any) {
        toast({
          title: t('봇 종료'),
          description:
            err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    } else {
      // 시작
      try {
        const { data: res } = await updateTradingSpot({
          symbol,
          botStatus: true,
          takeProfit: Number(takeProfit),
          stopLoss: Number(stopLoss),
          size: Number(size),
          roiTriggers: roiTriggers.map((item) => ({
            roi: Number(item.roi),
            size: Number(item.size)
          })),
          useStopLoss: useStopLoss,
          upbitAccessKey,
          upbitSecretKey
        })
        if (res.statusCode === 200) {
          toast({
            title: t('봇 시작'),
            description: res.message,
            status: 'success',
            duration: 2000,
            isClosable: true
          })
          reloadUser()
        }
      } catch (err: any) {
        toast({
          title: t('봇 시작'),
          description:
            err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }
    setIsRunning((prev) => !prev)
  }

  const handleBatchApply = () => {
    const count = Math.max(1, Number(batchCount))
    const roi = Math.max(1, Number(batchRoi))
    const size = Math.max(1, Number(batchSize))
    const newTriggers = Array.from({ length: count }, () => ({
      roi: String(roi),
      size: String(size)
    }))
    setRoiTriggers(newTriggers)
    onClose()
  }

  const addRoiTrigger = () => {
    const last = roiTriggers[roiTriggers.length - 1] || { roi: '30', size: '1' }
    setRoiTriggers([...roiTriggers, { roi: last.roi, size: last.size }])
  }

  const removeRoiTrigger = (index: number) => {
    const updated = [...roiTriggers]
    updated.splice(index, 1)
    setRoiTriggers(updated)
  }

  return (
    <DefaultLayout>
      <Box maxW="3xl" mx="auto" p={6}>
        {/* 심볼 선택 & 현재가 */}
        <Box mb={4}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            spacing={{ base: 3, md: 6 }}
          >
            {/* 코인 선택 */}
            <Box>
              <Text
                fontSize={{ base: 'lg', md: 'xl' }}
                fontWeight="bold"
                mb={2}
              >
                {t('거래 코인 선택')}
              </Text>

              {isMobile ? (
                <Wrap spacing={2}>
                  {AVAILABLE_SYMBOLS.map((sym) => (
                    <WrapItem key={sym}>
                      <Button
                        size="sm"
                        variant={symbol === sym ? 'solid' : 'outline'}
                        colorScheme={symbol === sym ? 'purple' : 'gray'}
                        onClick={() => setSymbol(sym)}
                      >
                        {t(SYMBOL_LABEL[sym])} ({sym})
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              ) : (
                <ButtonGroup isAttached>
                  {AVAILABLE_SYMBOLS.map((sym) => (
                    <Button
                      key={sym}
                      size="sm"
                      variant={symbol === sym ? 'solid' : 'outline'}
                      colorScheme={symbol === sym ? 'purple' : 'gray'}
                      onClick={() => setSymbol(sym)}
                    >
                      {t(SYMBOL_LABEL[sym])} ({sym})
                    </Button>
                  ))}
                </ButtonGroup>
              )}
            </Box>

            {/* 현재가/예상수량 */}
            <Box
              textAlign={{ base: 'left', md: 'right' }}
              bg="gray.50"
              borderRadius="md"
              p={3}
            >
              <HStack
                justify={{ base: 'space-between', md: 'flex-end' }}
                mb={1}
              >
                <Text fontSize="sm" color="gray.600">
                  {t('현재가 (KRW)')}
                </Text>
                <HStack>
                  <IconButton
                    aria-label={t('가격 새로고침')}
                    icon={<RepeatIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => fetchKrwPrice(symbol)}
                  />
                </HStack>
              </HStack>

              <Skeleton isLoaded={!loadingPrice}>
                <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold">
                  {krwPrice ? krwPrice.toLocaleString() : '-'}
                </Text>
              </Skeleton>

              {estimatedCoinAmount !== null && (
                <Tag
                  mt={2}
                  size={isMobile ? 'sm' : 'md'}
                  colorScheme="purple"
                  variant="subtle"
                  whiteSpace="nowrap"
                >
                  {t('1회 주문 예상 수량 ≈ {{amount}} {{symbol}}', {
                    amount: estimatedCoinAmount.toFixed(6),
                    symbol
                  })}
                </Tag>
              )}
            </Box>
          </Stack>
        </Box>

        {/* API Key & 보조 툴 */}
        <Box maxW="3xl" mx="auto" p={6} pt={3} pb={4}>
          <HStack spacing={4} align="start" flexWrap="wrap">
            {auth.id && <ApiManagerSpot />}
            <SizeCalculatorSpot
              symbol={symbol}
              priceKrwFromParent={krwPrice}
              onRefreshPrice={(s) => fetchKrwPrice(s)}
            />
          </HStack>

          {auth.id && (
            <Box mt={3}>
              <Button
                colorScheme={isRunning ? 'red' : 'green'}
                w={'100%'}
                onClick={() => {
                  setConfirmAction(isRunning ? 'stop' : 'start')
                  onConfirmOpen()
                }}
              >
                {isRunning ? t('자동매매 종료') : t('자동매매 시작')}
              </Button>
            </Box>
          )}
        </Box>

        {/* Trading Settings Section */}
        <Box mt={2}>
          <HStack justify="space-between" align="center" mb={4} flexWrap="wrap">
            <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold">
              {t('현물 자동매매 설정')}
            </Text>

            {auth.id && (
              <HStack spacing={2}>
                <Button
                  colorScheme="purple"
                  size="sm"
                  leftIcon={<IoMdSave />}
                  onClick={handleSave}
                >
                  {t('설정 저장')}
                </Button>
              </HStack>
            )}
          </HStack>

          {/* 익절 */}
          <Box mb={4}>
            <Text mb={1}>{t('익절 (%)')}</Text>
            <HStack mb={2} flexWrap="wrap">
              <Button
                size="xs"
                onClick={() =>
                  setTakeProfit((prev) => String(Math.max(1, Number(prev) - 1)))
                }
              >
                -1
              </Button>
              {[5, 6, 7, 8, 9, 10].map((val) => (
                <Button
                  key={val}
                  size="xs"
                  variant={takeProfit === String(val) ? 'solid' : 'outline'}
                  colorScheme={takeProfit === String(val) ? 'purple' : 'gray'}
                  onClick={() => setTakeProfit(String(val))}
                >
                  {val}
                </Button>
              ))}
              <Button
                size="xs"
                onClick={() => setTakeProfit(String(Number(takeProfit) + 1))}
              >
                +1
              </Button>
            </HStack>
            <Input
              type="number"
              inputMode="decimal"
              min={1}
              value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)}
              onBlur={() => {
                if (!takeProfit || Number(takeProfit) < 1) setTakeProfit('1')
              }}
            />
          </Box>

          {/* 손절 */}
          <Box mb={4}>
            <HStack justify="space-between" mb={1}>
              <Text>{t('손절 (%)')}</Text>
              <HStack spacing={2}>
                <Text fontSize="sm">{t('손절 기능 사용')}</Text>
                <Switch
                  isChecked={useStopLoss}
                  onChange={(e) => {
                    const isChecked = e.target.checked
                    setUseStopLoss(isChecked)
                    if (!isChecked) setStopLoss('0')
                  }}
                  colorScheme="purple"
                />
              </HStack>
            </HStack>

            <HStack
              mb={2}
              flexWrap="wrap"
              opacity={useStopLoss ? 1 : 0.4}
              pointerEvents={useStopLoss ? 'auto' : 'none'}
            >
              <Button
                size="xs"
                onClick={() =>
                  setStopLoss((prev) => String(Math.max(1, Number(prev) - 1)))
                }
              >
                -1
              </Button>
              {[5, 6, 7, 8, 9, 10].map((val) => (
                <Button
                  key={val}
                  size="xs"
                  variant={stopLoss === String(val) ? 'solid' : 'outline'}
                  colorScheme={stopLoss === String(val) ? 'purple' : 'gray'}
                  onClick={() => setStopLoss(String(val))}
                >
                  {val}
                </Button>
              ))}
              <Button
                size="xs"
                onClick={() => setStopLoss(String(Number(stopLoss) + 1))}
              >
                +1
              </Button>
            </HStack>

            <Input
              type="number"
              inputMode="decimal"
              min={1}
              value={stopLoss}
              onChange={(e) => setStopLoss(e.target.value)}
              onBlur={() => {
                if (!stopLoss || Number(stopLoss) < 1) setStopLoss('1')
              }}
              isDisabled={!useStopLoss}
              placeholder={t('손절 비율 입력')}
            />
          </Box>

          {/* 사이즈 (현물: 금액 기반) */}
          <Box mb={4}>
            <Text mb={1}>
              {t('사이즈 (1 Size = {{amount}} KRW)', {
                amount: SIZE_KRW.toLocaleString()
              })}
            </Text>
            <HStack align="center">
              <Input
                type="number"
                inputMode="decimal"
                min={1}
                value={size}
                onChange={(e) => setSize(e.target.value)}
                onBlur={() => {
                  if (!size || Number(size) < 1) setSize('1')
                }}
              />
              <IconButton
                aria-label={t('붙여넣기')}
                icon={<CopyIcon />}
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText()
                    const parsed = parseFloat(text)
                    if (!isNaN(parsed)) {
                      setSize(String(parsed))
                      toast({
                        title: t('사이즈 입력'),
                        description: t('"{{value}}" 값이 입력되었습니다.', {
                          value: parsed
                        }),
                        status: 'success',
                        duration: 2000,
                        isClosable: true
                      })
                    } else {
                      toast({
                        title: t('유효하지 않은 숫자입니다.'),
                        description: t('붙여넣은 값이 숫자가 아닙니다.'),
                        status: 'error',
                        duration: 2000,
                        isClosable: true
                      })
                    }
                  } catch (err) {
                    toast({
                      title: t('붙여넣기 실패'),
                      description: t('클립보드에서 값을 읽을 수 없습니다.'),
                      status: 'error',
                      duration: 2000,
                      isClosable: true
                    })
                  }
                }}
                size="sm"
                variant="ghost"
                colorScheme="purple"
              />
            </HStack>
            <Text mt={1} fontSize="sm" color="gray.600">
              {t('주문 1회 금액')}: <b>{computedOrderKRW.toLocaleString()}</b>{' '}
              KRW
            </Text>
          </Box>

          {/* 물타기 전략 */}
          <Box mt={8}>
            <HStack justify="space-between" mb={3}>
              <Text fontWeight="bold">{t('물타기 전략')}</Text>
              <HStack spacing={2}>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => setRoiTriggers([])}
                >
                  {t('초기화')}
                </Button>
                <Button size="xs" variant="outline" onClick={onOpen}>
                  {t('일괄 설정')}
                </Button>
              </HStack>
            </HStack>

            <VStack align="stretch" spacing={3}>
              {/* 헤더 */}
              <Box
                display="grid"
                gridTemplateColumns="80px 1fr 1fr 32px"
                px={1}
                fontWeight="semibold"
                color="gray.600"
              >
                <Text textAlign="center">{t('회차')}</Text>
                <Text textAlign="center">{t('ROI (%)')}</Text>
                <Text textAlign="center">{t('사이즈')}</Text>
                <Box />
              </Box>

              {roiTriggers.length === 0 ? (
                <Box textAlign="center" color="gray.500" py={4}>
                  {t('물타기 미사용 상태입니다.')}
                </Box>
              ) : (
                roiTriggers.map((item, index) => (
                  <Box
                    key={index}
                    display="grid"
                    gridTemplateColumns="80px 1fr 1fr 32px"
                    gap={2}
                    alignItems="center"
                  >
                    {/* 회차 */}
                    <Text textAlign="center">
                      {index + 1}
                      {t('회차')}
                    </Text>

                    {/* ROI 입력 */}
                    <Input
                      type="number"
                      min={1}
                      value={item.roi}
                      onChange={(e) => {
                        const updated = [...roiTriggers]
                        updated[index].roi = e.target.value
                        setRoiTriggers(updated)
                      }}
                      onBlur={() => {
                        if (!item.roi || Number(item.roi) < 1) {
                          const updated = [...roiTriggers]
                          updated[index].roi = '1'
                          setRoiTriggers(updated)
                        }
                      }}
                      placeholder="ROI"
                    />

                    {/* 사이즈 입력 */}
                    <Input
                      type="number"
                      min={1}
                      value={item.size}
                      onChange={(e) => {
                        const updated = [...roiTriggers]
                        updated[index].size = e.target.value
                        setRoiTriggers(updated)
                      }}
                      onBlur={() => {
                        if (!item.size || Number(item.size) < 1) {
                          const updated = [...roiTriggers]
                          updated[index].size = '1'
                          setRoiTriggers(updated)
                        }
                      }}
                      placeholder={t('사이즈')}
                    />

                    {/* 삭제 버튼 */}
                    <CloseButton onClick={() => removeRoiTrigger(index)} />
                  </Box>
                ))
              )}

              <Button
                leftIcon={<AddIcon />}
                w="100%"
                onClick={addRoiTrigger}
                colorScheme="purple"
                alignSelf="start"
              >
                {t('물타기 단계 추가')}
              </Button>
            </VStack>
          </Box>
        </Box>
      </Box>

      {/* 일괄 설정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('물타기 전략 일괄 설정')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <HStack w="100%">
                <Text w="120px">{t('물타기 회수')}</Text>
                <Input
                  type="number"
                  value={batchCount}
                  onChange={(e) => setBatchCount(e.target.value)}
                />
              </HStack>
              <HStack w="100%">
                <Text w="120px">{t('시작 ROI (%)')}</Text>
                <Input
                  type="number"
                  value={batchRoi}
                  onChange={(e) => setBatchRoi(e.target.value)}
                />
              </HStack>
              <HStack w="100%">
                <Text w="120px">{t('사이즈')}</Text>
                <Input
                  type="number"
                  value={batchSize}
                  onChange={(e) => setBatchSize(e.target.value)}
                />
              </HStack>
              <Text fontSize="sm" color="gray.600">
                {t('* 사이즈 1개당 주문금액 {{amount}} KRW', {
                  amount: SIZE_KRW.toLocaleString()
                })}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" onClick={handleBatchApply}>
              {t('적용하기')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 시작/종료 확인 모달 */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(confirmAction === 'start' ? '자동매매 시작' : '자동매매 종료')}{' '}
            {t('확인')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={1}>
              {t('정말로 자동매매를 {{action}} 하시겠습니까?', {
                action: confirmAction === 'start' ? t('시작') : t('종료')
              })}
            </Text>

            {confirmAction === 'start' && (
              <Box bg="gray.50" borderRadius="md" p={4}>
                <Text fontWeight="bold" mb={2}>
                  {t('현재 설정 요약')}
                </Text>
                <Stack spacing={1}>
                  <HStack justify="space-between">
                    <Text>{t('코인')}</Text>
                    <Text>
                      {t(SYMBOL_LABEL[symbol])} ({symbol})
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('익절')}</Text>
                    <Text>{takeProfit}%</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('손절')}</Text>
                    <Text>{useStopLoss ? `${stopLoss}%` : t('미사용')}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('사이즈 (개수)')}</Text>
                    <Text>
                      {size}
                      {t('개')} ({t('총')} {computedOrderKRW.toLocaleString()}{' '}
                      KRW)
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('물타기 회수')}</Text>
                    <Text>
                      {roiTriggers.length}
                      {t('회')}
                    </Text>
                  </HStack>
                </Stack>
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onConfirmClose} variant="ghost" mr={3}>
              {t('취소')}
            </Button>
            <Button
              colorScheme={confirmAction === 'start' ? 'green' : 'red'}
              onClick={() => {
                handleToggleAutoTrade()
                onConfirmClose()
              }}
            >
              {t('확인')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default Spot
